import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface BroadcastRow {
  readonly id: string;
  readonly title: string;
  readonly scope: string;
  readonly status: string;
  readonly createdAt: string;
}

export function NotificationsBroadcastsPage(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<BroadcastRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [broadcastCode, setBroadcastCode] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState('ALL');
  const [channels, setChannels] = useState('email');
  const [scheduledAt, setScheduledAt] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(false);

  const load = (): void => {
    apiRequest<{ items: BroadcastRow[] }>('/notifications/broadcasts', { token })
      .then((r) => setRows(r.items))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const onCompose = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreating(true);
    setCreated(false);
    try {
      const channelList = channels.split(/[\s,]+/).filter(Boolean);
      await apiRequest('/notifications/broadcasts', {
        method: 'POST',
        token,
        body: {
          broadcastCode,
          title,
          body,
          scope,
          channels: channelList.length > 0 ? channelList : undefined,
          scheduledAt: scheduledAt || undefined,
        },
      });
      setCreated(true);
      setBroadcastCode('');
      setTitle('');
      setBody('');
      setScheduledAt('');
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const approve = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/notifications/broadcasts/${id}/approve`, { token, method: 'POST' });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const send = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await apiRequest(`/notifications/broadcasts/${id}/send`, { token, method: 'POST' });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="broadcasts">
      <h1>{t('notif.broadcasts.title')}</h1>

      <form onSubmit={onCompose} aria-label="broadcast-compose-form">
        <h2>{t('notif.broadcasts.compose')}</h2>
        <label htmlFor="bc-code">{t('notif.broadcasts.broadcastCode')}</label>
        <input id="bc-code" value={broadcastCode} onChange={(e) => setBroadcastCode(e.target.value)} required />
        <label htmlFor="bc-title">{t('notif.common.title')}</label>
        <input id="bc-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label htmlFor="bc-body">{t('notif.broadcasts.message')}</label>
        <textarea id="bc-body" value={body} onChange={(e) => setBody(e.target.value)} required />
        <label htmlFor="bc-scope">{t('notif.common.scope')}</label>
        <select id="bc-scope" aria-label="broadcast-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="ALL">{t('notif.broadcasts.scopeAll')}</option>
          <option value="ROLE">{t('notif.broadcasts.scopeRole')}</option>
          <option value="TENANT">{t('notif.broadcasts.scopeTenant')}</option>
        </select>
        <label htmlFor="bc-channels">{t('notif.common.channels')}</label>
        <input id="bc-channels" value={channels} onChange={(e) => setChannels(e.target.value)} />
        <label htmlFor="bc-scheduled">{t('notif.broadcasts.scheduleAt')}</label>
        <DateInput id="bc-scheduled" type="datetime-local" value={scheduledAt} onValueChange={(nextValue) => setScheduledAt(nextValue)} />
        {error ? <p role="alert">{error}</p> : null}
        {created ? <p role="status">{t('notif.broadcasts.created')}</p> : null}
        <Button type="submit" disabled={creating} variant="primary">{creating ? t('notif.common.creating') : t('notif.broadcasts.createBroadcast')}</Button>
      </form>

      <DataGrid<any>
        caption="broadcasts-table"
        rows={rows}
        getRowId={(r) => r.id}
        columns={[
          { key: 'created', header: t('notif.common.created'), value: (r) => r.createdAt, render: (r) => at(r.createdAt) },
          { key: 'title', header: t('notif.common.title'), value: (r) => r.title },
          { key: 'scope', header: t('notif.common.scope'), value: (r) => r.scope },
          { key: 'status', header: t('notif.common.status'), value: (r) => r.status },
        ]}
        actionsHeader={t('notif.common.action')}
        actions={(r) => (
          <>
            {r.status === 'PENDING_APPROVAL' && (
              <Button type="button" disabled={busyId === r.id} onClick={() => void approve(r.id)} variant="secondary">
                {busyId === r.id ? t('notif.broadcasts.approving') : t('notif.broadcasts.approve')}
              </Button>
            )}
            {r.status === 'APPROVED' && (
              <Button type="button" disabled={busyId === r.id} onClick={() => void send(r.id)} variant="primary">
                {busyId === r.id ? t('notif.broadcasts.sending') : t('notif.broadcasts.send')}
              </Button>
            )}
          </>
        )}
      />
    </main>
  );
}
